import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	setBodyGradient,
	setBorderRadius,
	setBorderStroke,
	setBoxLayout,
	setIconColor,
	setMonoChrome,
	setThemeColor,
	setThemeSettingsData,
	setSelectedMod,
	setCardShadow,
	setSidebarDark,
	setHeaderDark
} from '../../Redux/actions/settingsActions';
import { getThemeSettings, updateThemeSettings } from 'adminApi/ThemeSettingApi/ThemeSetting';
import { showError, showSuccess } from 'utils/notifications';

const CommonSetting = () => {
	const dispatch = useDispatch();

	// Individual memoized selectors to prevent unnecessary re-renders
	const themeMode = useSelector(state => state.themeMode?.themeMode || 'light');
	const themeColor = useSelector(state => state.theme?.themeColor || 'PurpleHeart');
	const borderStroke = useSelector(state => state.stroke?.borderStroke || 'svgstroke-a');
	const boxLayout = useSelector(state => state.boxLayout?.boxLayout || 'off');
	const monochrome = useSelector(state => state.monochrome?.monochrome || 'off');
	const borderRadius = useSelector(state => state.borderRadius?.borderRadius || 'off');
	const iconColor = useSelector(state => state.iconColor?.iconColor || 'off');
	const gradientColor = useSelector(state => state.gradientColor?.gradientColor || 'bg-gradient');
	const cardShadow = useSelector(state => state.cardShadow?.cardShadow || 'off');
	const sidebarDark = useSelector(state => state.sidebarDark?.sidebarDark || 'none');
	const headerDark = useSelector(state => state.headerDark?.headerDark || 'none');

	// Memoized theme settings object to prevent reference changes
	const themeSettings = useMemo(() => ({
		themeMode,
		themeColor,
		borderStroke,
		boxLayout,
		monochrome,
		borderRadius,
		iconColor,
		gradientColor,
		cardShadow,
		sidebarDark,
		headerDark
	}), [themeMode, themeColor, borderStroke, boxLayout, monochrome, borderRadius, iconColor, gradientColor, cardShadow, sidebarDark, headerDark]);

	// Loading and error states
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);

	// Track if this is the initial load to prevent duplicate API calls
	const hasInitialized = useRef(false);

	// Local state for UI controls (synchronized with Redux)
	const [theme, setTheme] = useState(themeSettings.themeColor);
	const [themeModeLocal, setThemeModeLocal] = useState(themeSettings.themeMode);
	const [borderStrokeLocal, setBorderStrokLocal] = useState(themeSettings.borderStroke);
	const [boxLayoutLocal, setBoxLayoutLocal] = useState(themeSettings.boxLayout);
	const [monochromeLocal, setMonoChromLocal] = useState(themeSettings.monochrome);
	const [borderRadiusLocal, setBorderRadiussLocal] = useState(themeSettings.borderRadius);
	const [iconColorLocal, setIconColorLocal] = useState(themeSettings.iconColor);
	const [gradientColorLocal, setGradientColorLocal] = useState(themeSettings.gradientColor);
	const [cardShadowLocal, setCardShadowLocal] = useState(themeSettings.cardShadow);
	const [sidebarDarkLocal, setSidebarDarkLocal] = useState(themeSettings.sidebarDark);
	const [headerDarkLocal, setHeaderDarkLocal] = useState(themeSettings.headerDark);

	// Track if component is initialized to prevent unnecessary updates
	const isInitialized = useRef(false);

	// Memoized theme color states for better performance
	const themeColors = useMemo(() => ({
		ValenciaRed: theme === 'ValenciaRed',
		SunOrange: theme === 'SunOrange',
		AppleGreen: theme === 'AppleGreen',
		CeruleanBlue: theme === 'CeruleanBlue',
		Mariner: theme === 'Mariner',
		PurpleHeart: theme === 'PurpleHeart',
		FrenchRose: theme === 'FrenchRose'
	}), [theme]);

	// Fetch theme settings from API - memoized for stability
	const fetchThemeSettingsFromAPI = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await getThemeSettings();

			if (response.data.success && response.data.data) {
				const themeData = response.data.data;
				dispatch(setThemeSettingsData(themeData));
				if (!hasInitialized.current) {
					hasInitialized.current = true;
				}
			} else {
				console.error('Failed to fetch theme settings:', response.data.error);
				setError('Failed to load theme settings');
			}
		} catch (error) {
			console.error('Error fetching theme settings:', error);
			setError('Failed to load theme settings');
		} finally {
			setLoading(false);
		}
	}, [dispatch]);

	// Load theme settings on component mount only once
	useEffect(() => {
		if (!hasInitialized.current) {
			fetchThemeSettingsFromAPI();
		}
	}, [fetchThemeSettingsFromAPI]);

	// Parameterized DOM update functions
	const updateCardShadowDOM = useCallback((isEnabled) => {
		const cardElements = document.getElementsByClassName('card');
		Array.from(cardElements).forEach(element => {
			if (isEnabled === 'on') {
				element.classList.add('shadow-active');
			} else {
				element.classList.remove('shadow-active');
			}
		});
	}, []);

	const updateElementTheme = useCallback((elementId, theme) => {
		const element = document.getElementById(elementId);
		if (element) {
			element.setAttribute('data-bs-theme', theme || 'none');
		}
	}, []);

	// Synchronize local state with Redux state when Redux changes - optimized with single effect
	useEffect(() => {
		// Allow DOM synchronization on initial load when API data arrives
		if (!isInitialized.current) {
			isInitialized.current = true;
		}

		// Only update local state if Redux values have actually changed
		if (theme !== themeSettings.themeColor) {
			setTheme(themeSettings.themeColor);
		}
		if (themeModeLocal !== themeSettings.themeMode) {
			setThemeModeLocal(themeSettings.themeMode);
		}
		if (borderStrokeLocal !== themeSettings.borderStroke) {
			setBorderStrokLocal(themeSettings.borderStroke);
		}
		if (boxLayoutLocal !== themeSettings.boxLayout) {
			setBoxLayoutLocal(themeSettings.boxLayout);
		}
		if (monochromeLocal !== themeSettings.monochrome) {
			setMonoChromLocal(themeSettings.monochrome);
		}
		if (borderRadiusLocal !== themeSettings.borderRadius) {
			setBorderRadiussLocal(themeSettings.borderRadius);
		}
		if (iconColorLocal !== themeSettings.iconColor) {
			setIconColorLocal(themeSettings.iconColor);
		}
		if (gradientColorLocal !== themeSettings.gradientColor) {
			setGradientColorLocal(themeSettings.gradientColor);
		}
		if (cardShadowLocal !== themeSettings.cardShadow) {
			setCardShadowLocal(themeSettings.cardShadow);
			updateCardShadowDOM(themeSettings.cardShadow);
		}
		if (sidebarDarkLocal !== themeSettings.sidebarDark) {
			setSidebarDarkLocal(themeSettings.sidebarDark);
			updateElementTheme('sidebarDark', themeSettings.sidebarDark);
		}
		if (headerDarkLocal !== themeSettings.headerDark) {
			setHeaderDarkLocal(themeSettings.headerDark);
			updateElementTheme('headerDark', themeSettings.headerDark);
		}
	}, [themeSettings, theme, themeModeLocal, borderStrokeLocal, boxLayoutLocal, monochromeLocal, borderRadiusLocal, iconColorLocal, gradientColorLocal, cardShadowLocal, sidebarDarkLocal, headerDarkLocal, updateCardShadowDOM, updateElementTheme]);

	// Dedicated DOM synchronization effect for theme settings - runs when API data loads
	useEffect(() => {
		// Apply card shadow to all card elements
		updateCardShadowDOM(themeSettings.cardShadow);

		// Apply sidebar dark theme
		updateElementTheme('sidebarDark', themeSettings.sidebarDark);

		// Apply header dark theme
		updateElementTheme('headerDark', themeSettings.headerDark);
	}, [themeSettings.cardShadow, themeSettings.sidebarDark, themeSettings.headerDark, updateCardShadowDOM, updateElementTheme]);

	// Border stroke change handler
	const handleStrokeChange = (e) => {
		const value = e.target.value;
		setBorderStrokLocal(value);
		dispatch(setBorderStroke(value));
	};

	// Toggle handlers with consistent logic - memoized for performance
	const toggleSetting = useCallback((currentValue, onValue, offValue, setter, action) => {
		const newValue = currentValue === onValue ? offValue : onValue;
		setter(newValue);
		dispatch(action(newValue));
	}, [dispatch]);

	const toggleBodyGradient = (e) => {
		toggleSetting(
			gradientColorLocal,
			'bg-gradient',
			'bg-body',
			setGradientColorLocal,
			setBodyGradient
		);
	};

	const toggleBoxLayout = (e) => {
		toggleSetting(
			boxLayoutLocal,
			'on',
			'off',
			setBoxLayoutLocal,
			setBoxLayout
		);
	};

	const toggleMonochrome = (e) => {
		toggleSetting(
			monochromeLocal,
			'on',
			'off',
			setMonoChromLocal,
			setMonoChrome
		);
	};

	const toggleBorderRadius = (e) => {
		toggleSetting(
			borderRadiusLocal,
			'on',
			'off',
			setBorderRadiussLocal,
			setBorderRadius
		);
	};

	const toggleIconColor = (e) => {
		toggleSetting(
			iconColorLocal,
			'on',
			'off',
			setIconColorLocal,
			setIconColor
		);
	};

	const toggleCardShadow = (e) => {
		const newCardShadow = cardShadowLocal === 'on' ? 'off' : 'on';

		// Update DOM first for immediate visual feedback
		updateCardShadowDOM(newCardShadow);

		// Update local state and Redux
		setCardShadowLocal(newCardShadow);
		dispatch(setCardShadow(newCardShadow));
	};

	// Layout Section Light/Dark
	const toggleThemeElement = (elementId) => {
		const element = document.getElementById(elementId);
		if (element) {
			const currentTheme = element.getAttribute('data-bs-theme');
			const newTheme = currentTheme === 'none' ? 'dark' : 'none';
			updateElementTheme(elementId, newTheme);
			return newTheme;
		}
		return 'none';
	};

	const toggleSidebarDark = (e) => {
		const newTheme = toggleThemeElement('sidebarDark');
		toggleSetting(
			sidebarDarkLocal,
			'dark',
			'none',
			setSidebarDarkLocal,
			setSidebarDark
		);
	};

	const toggleHeaderDark = (e) => {
		const newTheme = toggleThemeElement('headerDark');
		toggleSetting(
			headerDarkLocal,
			'dark',
			'none',
			setHeaderDarkLocal,
			setHeaderDark
		);
	};

	// Theme change handler - memoized for performance
	const handleThemeChange = useCallback((newTheme) => {
		setTheme(newTheme);
		dispatch(setThemeColor(newTheme));
	}, [dispatch]);

	// Theme mode change handler - memoized for performance
	const handleThemeModeChange = useCallback((newMode) => {
		setThemeModeLocal(newMode);
		dispatch(setSelectedMod(newMode));
		// Apply theme mode to the document
		document.documentElement.setAttribute('data-bs-theme', newMode);
	}, [dispatch]);

	// Save theme settings to API - memoized for performance
	const saveThemeSettings = useCallback(async () => {
		setSaving(true);
		setError(null);
		try {
			const themeSettingsPayload = {
				themeMode: themeModeLocal,
				themeColor: theme,
				borderStroke: borderStrokeLocal,
				boxLayout: boxLayoutLocal,
				monochrome: monochromeLocal,
				borderRadius: borderRadiusLocal,
				iconColor: iconColorLocal,
				cardShadow: cardShadowLocal,
				gradientColor: gradientColorLocal,
				sidebarDark: sidebarDarkLocal,
				headerDark: headerDarkLocal
			};

			const res = await updateThemeSettings(themeSettingsPayload);
			if (res.data.success) {
				fetchThemeSettingsFromAPI();
				// Close the offcanvas after successful save
				setTimeout(() => {
					const offcanvasElement = document.getElementById('offcanvas_setting');
					if (offcanvasElement) {
						const closeButton = offcanvasElement.querySelector('[data-bs-dismiss="offcanvas"]');
						if (closeButton) {
							closeButton.click();
						}
					}
				}, 200);
				showSuccess(res.data.message || 'Theme settings saved successfully');
			} else {
				console.error('Failed to save theme settings:', res.data.error);
				showError(res.data.error || 'Failed to save theme settings');
			}
		} catch (error) {
			console.error('Error saving theme settings:', error);
			setError('Failed to save theme settings');
			showError(error.message || 'Failed to save theme settings');
		} finally {
			setSaving(false);
		}
	}, [themeModeLocal, theme, borderStrokeLocal, boxLayoutLocal, monochromeLocal, borderRadiusLocal, iconColorLocal, gradientColorLocal, cardShadowLocal, sidebarDarkLocal, headerDarkLocal]);

	return (
		<div className="offcanvas offcanvas-end" tabIndex="-1" id="offcanvas_setting" aria-labelledby="offcanvas_setting">
			<div className="offcanvas-header">
				<h5 className="offcanvas-title">Template Setting</h5>
				<button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div className="offcanvas-body">
				<div className="mb-4">
					<h6>Theme Mode</h6>
					<div className="btn-group w-100" role="group" aria-label="Theme mode toggle">
						<input
							type="radio"
							className="btn-check"
							name="themeMode"
							id="themeModeLight"
							value="light"
							checked={themeModeLocal === 'light'}
							onChange={() => handleThemeModeChange('light')}
							disabled={loading}
						/>
						<label className="btn btn-outline-primary" htmlFor="themeModeLight">
							<i className="bi bi-sun me-2"></i>Light
						</label>

						<input
							type="radio"
							className="btn-check"
							name="themeMode"
							id="themeModeDark"
							value="dark"
							checked={themeModeLocal === 'dark'}
							onChange={() => handleThemeModeChange('dark')}
							disabled={loading}
						/>
						<label className="btn btn-outline-primary" htmlFor="themeModeDark">
							<i className="bi bi-moon me-2"></i>Dark
						</label>

						<input
							type="radio"
							className="btn-check"
							name="themeMode"
							id="themeModeAuto"
							value="auto"
							checked={themeModeLocal === 'auto'}
							onChange={() => handleThemeModeChange('auto')}
							disabled={loading}
						/>
						<label className="btn btn-outline-primary" htmlFor="themeModeAuto">
							<i className="bi bi-circle-half me-2"></i>Auto
						</label>
					</div>
				</div>
				<div className="mb-4">
					<h6>Set Theme Color</h6>
					<ul className="choose-skin list-unstyled mb-0">
						<li className={`${themeColors.ValenciaRed ? 'active' : ''}`} onClick={() => handleThemeChange('ValenciaRed')} data-theme="ValenciaRed"><div style={{ "--patidar-samaj-theme-color": "#D63B38" }}></div></li>
						<li className={`${themeColors.SunOrange ? 'active' : ''}`} onClick={() => handleThemeChange('SunOrange')} data-theme="SunOrange"><div style={{ "--patidar-samaj-theme-color": "#F7A614" }}></div></li>
						<li className={`${themeColors.AppleGreen ? 'active' : ''}`} onClick={() => handleThemeChange('AppleGreen')} data-theme="AppleGreen"><div style={{ "--patidar-samaj-theme-color": "#5BC43A" }}></div></li>
						<li className={`${themeColors.CeruleanBlue ? 'active' : ''}`} onClick={() => handleThemeChange('CeruleanBlue')} data-theme="CeruleanBlue"><div style={{ "--patidar-samaj-theme-color": "#00B8D6" }}></div></li>
						<li className={`${themeColors.Mariner ? 'active' : ''}`} onClick={() => handleThemeChange('Mariner')} data-theme="Mariner"><div style={{ "--patidar-samaj-theme-color": "#0066FE" }}></div></li>
						<li className={`${themeColors.PurpleHeart ? 'active' : ''}`} onClick={() => handleThemeChange('PurpleHeart')} data-theme="PurpleHeart"><div style={{ "--patidar-samaj-theme-color": "#6238B3" }}></div></li>
						<li className={`${themeColors.FrenchRose ? 'active' : ''}`} onClick={() => handleThemeChange('FrenchRose')} data-theme="FrenchRose"><div style={{ "--patidar-samaj-theme-color": "#EB5393" }}></div></li>
					</ul>
				</div>
				<div className="svg-stroke mb-4">
					<h6>Icon Border Stroke</h6>
					<div className="btn-group" role="group" aria-label="Basic radio toggle button group">
						<input checked={borderStrokeLocal === 'svgstroke-a'} onChange={handleStrokeChange} type="radio" className="btn-check" name="stroke" id="Stroke_A" value="svgstroke-a" />
						<label className="btn btn-outline-primary" htmlFor="Stroke_A">Stroke 1</label>

						<input checked={borderStrokeLocal === 'svgstroke-b'} onChange={handleStrokeChange} type="radio" className="btn-check" name="stroke" id="Stroke_B" value="svgstroke-b" />
						<label className="btn btn-outline-primary" htmlFor="Stroke_B">Stroke 2</label>

						<input checked={borderStrokeLocal === 'svgstroke-c'} onChange={handleStrokeChange} type="radio" className="btn-check" name="stroke" id="Stroke_C" value="svgstroke-c" />
						<label className="btn btn-outline-primary" htmlFor="Stroke_C">Stroke 3</label>
					</div>
				</div>
				<div className="mb-4">
					<h6>Layout Section Ligh/Dark</h6>
					<div className="form-check form-switch sidebar-dark-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="sidebarDark" onChange={toggleSidebarDark} checked={sidebarDarkLocal === 'dark'} disabled={loading} />
						<label className="form-check-label" htmlFor="sidebarDark">Sidebar Dark</label>
					</div>
					<div className="form-check form-switch header-dark-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="headerDark" onChange={toggleHeaderDark} checked={headerDarkLocal === 'dark'} disabled={loading} />
						<label className="form-check-label" htmlFor="headerDark">Header Dark</label>
					</div>
				</div>
				<div className="mb-4">
					<h6>More App Setting</h6>
					<div className="form-check form-switch bodygradient-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="bodyGradient" onChange={toggleBodyGradient} checked={gradientColorLocal === 'bg-gradient'} disabled={loading} />
						<label className="form-check-label" htmlFor="bodyGradient">Body Gradient Background</label>
					</div>
					<div className="form-check form-switch boxlayout-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="boxlayout" onChange={toggleBoxLayout} checked={boxLayoutLocal === 'on'} disabled={loading} />
						<label className="form-check-label" htmlFor="boxlayout">Box Layout</label>
					</div>
					<div className="form-check form-switch monochrome-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="monochrome" onChange={toggleMonochrome} checked={monochromeLocal === 'on'} disabled={loading} />
						<label className="form-check-label" htmlFor="monochrome">Monochrome Mode</label>
					</div>
					<div className="form-check form-switch radius-toggle">
						<input className="form-check-input" type="checkbox" role="switch" id="radius0" onChange={toggleBorderRadius} checked={borderRadiusLocal === 'on'} disabled={loading} />
						<label className="form-check-label" htmlFor="radius0">Border Radius none</label>
					</div>
					<div className="form-check form-switch svg-icon-color">
						<input className="form-check-input" type="checkbox" role="switch" id="IconColor" onChange={toggleIconColor} checked={iconColorLocal === 'on'} disabled={loading} />
						<label className="form-check-label" htmlFor="IconColor">Sidebar Icon color</label>
					</div>
					<div className="form-check form-switch cb-shadow">
						<input className="form-check-input" type="checkbox" role="switch" id="BoxShadow" onChange={toggleCardShadow} checked={cardShadowLocal === 'on'} disabled={loading} />
						<label className="form-check-label" htmlFor="BoxShadow">Card box shadow active</label>
					</div>
				</div>
				{error && (
					<div className="alert alert-danger" role="alert">
						{error}
					</div>
				)}
				<div className="d-flex mt-auto">
					<button
						type="button"
						className="btn w-100 py-2 btn-primary"
						onClick={saveThemeSettings}
						disabled={saving || loading}
					>
						{saving ? (
							<>
								<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
								Saving...
							</>
						) : (
							'Save Settings'
						)}
					</button>
				</div>
			</div>
		</div>
	)
}

export default CommonSetting
